import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';

// Actions
import { attachProfileToPortfolio } from '@/redux/modules/edit-portfolio';
import { deletePortfolioRiskProfile } from '@/redux/modules/edit-portfolio';
import {
  loadKycQuestionnaire,
  resetRiskProfile,
  setRecommendedRiskProfile,
  setRiskProfileForEdit,
  getKycQuestionnaires,
  SET_IS_RESETTING_RISK_PROFILE,
} from '@/redux/modules/kyc-questionnaire';
import { selectIsLoadingSome } from '@/redux/modules/ui/loaders';
import { SET_IS_ATTACHING_PROFILE_TO_PORTFOLIO } from '@/redux/modules/edit-portfolio';

// Components
import CreateKycQuestionnaire from '@/views/components/modals/CreateKycQuestionnaire';
import DiscardPortfolioRiskProfileModal from '@/views/components/modals/DiscardPortfolioRiskProfileModal';
import DropdownButton from '@/views/components/buttons/DropdownButton';
import { SecondaryButton } from '@/views/components/buttons';
import { withErrorBoundary } from '@/views/components/errors';
import { Tooltip } from '@/views/components/tooltips';

// Types
import ContactRiskProfile from '@/types/contact-risk-profile';
import KycQuestionnaire from '@/types/kyc-questionnaire';
import Locale from '@/types/locale';
import Portfolio from '@/types/portfolio';
import PortfolioRiskProfile from '@/types/portfolio-risk-profile';
import { ReduxState } from '@/redux/modules/types';

// Utils
import { formatLocalizedString, formatMessage, FormattedMessage } from '@/utils/i18n';
import { getActiveQuestionnaire } from '@/utils/risk-profiles';
import notification from '@/utils/notification';
import { PERMISSIONS, userHasPermissionEnabled } from '@/utils/permissions';
import RiskProfileItem, { RISK_SCORE_TYPE, SelectedRiskProfile } from '@/types/risk-profile-item';
import Nullable from '@/types/nullable';
import User from '@/types/user';
import { addUpRiskToProfile } from '@/utils/up-risk';

type DispatchProps = {
  attachProfileToPortfolio: (profile: Partial<PortfolioRiskProfile>) => void;
  deletePortfolioRiskProfile: () => void;
  loadKycQuestionnaire: (questionnaire: KycQuestionnaire) => void;
  resetRiskProfile: () => void;
  setRiskProfileForEdit: (profile: PortfolioRiskProfile) => void;
  setRecommendedRiskProfile: (riskProfile: SelectedRiskProfile) => void;
  getKycQuestionnaires: () => Promise<KycQuestionnaire[]>;
};

type StateProps = {
  currentQuestionnaire?: KycQuestionnaire;
  isSavingRiskProfile: boolean;
  locale: Locale;
  newRiskProfile: Partial<ContactRiskProfile>;
  recommendedRiskProfile?: RiskProfileItem;
  user?: Nullable<User>;
  portfolio: Portfolio;
  questionnaires: KycQuestionnaire[];
  portfolioHasMissingHoldings?: boolean;
};

type Props = StateProps & DispatchProps;

const PortfolioEditorRiskProfilePicker = (props: Props) => {
  const {
    currentQuestionnaire,
    isSavingRiskProfile,
    locale,
    newRiskProfile,
    portfolio,
    questionnaires,
    user,
    attachProfileToPortfolio,
    deletePortfolioRiskProfile,
    loadKycQuestionnaire,
    resetRiskProfile,
    setRiskProfileForEdit,
    setRecommendedRiskProfile,
    portfolioHasMissingHoldings,
    getKycQuestionnaires,
  } = props;

  const { riskProfile, planning } = portfolio;
  const [showKycQuestionnaireModal, setShowKycQuestionnaireModal] = useState<boolean>(false);
  const [showDiscardRiskProfileModal, setShowDiscardRiskProfileModal] = useState<boolean>(false);

  // Fetch questionnaires on component mount if not already loaded
  useEffect(() => {
    if (!questionnaires || questionnaires.length === 0) {
      getKycQuestionnaires();
    }
  }, [getKycQuestionnaires, questionnaires]);

  const createDropdownItems = [
    {
      node: formatMessage('PortfolioEditorBuilder.RiskProfile.Edit'),
      disabled: !riskProfile?.profile,
      icon: 'pencil',
      onClick: () => {
        if (riskProfile && currentQuestionnaire) {
          setRiskProfileForEdit(riskProfile);
          setShowKycQuestionnaireModal(true);

          if (userHasPermissionEnabled(user, PERMISSIONS.KYC_UP_RISK_WARNING) && riskProfile.profile) {
            setRecommendedRiskProfile(riskProfile.profile);
          }
        }
      },
    },
    {
      node: formatMessage('PortfolioEditorBuilder.RiskProfile.Remove'),
      disabled: false,
      icon: 'remove-new',
      onClick: () => setShowDiscardRiskProfileModal(true),
    },
  ];

  const setKycQuestionnaire = useCallback(() => {
    const questionnaire = getActiveQuestionnaire(questionnaires);
    resetRiskProfile();

    if (questionnaire) {
      loadKycQuestionnaire(questionnaire);
    }
  }, [questionnaires, loadKycQuestionnaire, resetRiskProfile]);

  useEffect(() => {
    setKycQuestionnaire();
  }, [setKycQuestionnaire, questionnaires]);

  const onCancelRiskProfile = () => {
    setShowKycQuestionnaireModal(false);
    resetRiskProfile();
  };

  const onDeletePortfolioRiskProfile = () => {
    setShowDiscardRiskProfileModal(false);
    deletePortfolioRiskProfile();
  };

  const onSaveRiskProfile = () => {
    const { user, newRiskProfile, recommendedRiskProfile } = props;
    setShowKycQuestionnaireModal(false);
    if (!newRiskProfile) {
      notification.errorFromKey('DEFAULT_ERROR');
      return;
    }

    if (newRiskProfile?.profile && recommendedRiskProfile && userHasPermissionEnabled(user, PERMISSIONS.KYC_UP_RISK_WARNING)) {
      addUpRiskToProfile(newRiskProfile.profile, recommendedRiskProfile, RISK_SCORE_TYPE.ORDER);
    }
    attachProfileToPortfolio(newRiskProfile);
    resetRiskProfile();
  };

  const setRiskProfileButtonText = (isLocked?: boolean) => (
    <div className="risk-profile-button-text">
      <span>
        <FormattedMessage id="PortfolioEditorBuilder.RiskProfile.RiskProfileTitle" />:
      </span>
      <span className="risk-profile-title">
        {riskProfile?.profile?.title ? (
          formatLocalizedString(riskProfile.profile.title, locale)
        ) : (
          <FormattedMessage id="PortfolioEditorBuilder.RiskProfile.Assign" />
        )}
      </span>
      {isLocked && (
        <Tooltip message={formatMessage('PortfolioEditorBuilder.RiskProfile.RiskProfileLockedTooltip')}>
          <i className="icon-lock" />
        </Tooltip>
      )}
    </div>
  );

  const displayRiskProfileValue = () => {
    if (planning?.planAccount) {
      return <SecondaryButton className="secondary-v2 visually-disabled">{setRiskProfileButtonText(true)}</SecondaryButton>;
    }

    if (riskProfile) {
      return (
        <DropdownButton toggle={{ component: SecondaryButton, className: 'edit-risk-profile secondary-v2' }} align="end" items={createDropdownItems}>
          {setRiskProfileButtonText()}
        </DropdownButton>
      );
    }

    return (
      <SecondaryButton
        className="secondary-v2"
        onClick={() => {
          if (currentQuestionnaire) {
            setShowKycQuestionnaireModal(true);
          }
        }}
        disabled={portfolioHasMissingHoldings}
      >
        {setRiskProfileButtonText()}
      </SecondaryButton>
    );
  };

  return (
    <div className="portfolio-editor-risk-profile">
      {displayRiskProfileValue()}
      {showKycQuestionnaireModal && currentQuestionnaire && (
        <CreateKycQuestionnaire
          isModal
          show={showKycQuestionnaireModal}
          riskProfiles={currentQuestionnaire.riskProfiles}
          newRiskProfile={newRiskProfile}
          minRiskCalculation={currentQuestionnaire.options.minRiskCalculation}
          scoreCalculation={currentQuestionnaire.options.scoreCalculation}
          onSave={onSaveRiskProfile}
          onClose={onCancelRiskProfile}
          isSaving={isSavingRiskProfile}
        />
      )}
      {showDiscardRiskProfileModal && riskProfile && (
        <DiscardPortfolioRiskProfileModal
          backdropClassName="modal-v2-backdrop"
          riskProfileLabel={formatLocalizedString(riskProfile.profile.title)}
          show={showDiscardRiskProfileModal}
          onCancel={() => setShowDiscardRiskProfileModal(false)}
          onConfirm={onDeletePortfolioRiskProfile}
        />
      )}
    </div>
  );
};

const mapStateToProps = (state: ReduxState): StateProps => {
  const { editPortfolio, intl } = state;
  return {
    user: state.auth.user,
    currentQuestionnaire: state.kycQuestionnaire.currentQuestionnaire,
    isSavingRiskProfile: selectIsLoadingSome(state, [SET_IS_ATTACHING_PROFILE_TO_PORTFOLIO.type, SET_IS_RESETTING_RISK_PROFILE.type]),
    locale: intl.locale,
    newRiskProfile: state.kycQuestionnaire.newRiskProfile,
    recommendedRiskProfile: state.kycQuestionnaire.recommendedRiskProfile,
    portfolio: editPortfolio.portfolio,
    questionnaires: state.kycQuestionnaire.questionnaires,
  };
};

export default connect(mapStateToProps, {
  attachProfileToPortfolio,
  deletePortfolioRiskProfile,
  getKycQuestionnaires,
  loadKycQuestionnaire,
  resetRiskProfile,
  setRiskProfileForEdit,
  setRecommendedRiskProfile,
})(withErrorBoundary(PortfolioEditorRiskProfilePicker));
